import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { StatusPill } from "@/components/StatusPill";
import { SelectField } from "@/pages/FacilitiesPage";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, AlertOctagon } from "lucide-react";

const SEVERITY_TONE = { low: "gray", normal: "gray", high: "orange", critical: "red" };
const STATUS_TONE = { open: "red", in_progress: "orange", resolved: "green" };

export default function IssuesPage() {
  const [issues, setIssues] = useState([]);
  const [facs, setFacs] = useState([]);
  const [nps, setNps] = useState([]);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", severity: "normal", status: "open", facility_id: "", np_id: "", due_date: "" });

  const load = useCallback(async () => {
    try {
      const [{ data: i }, { data: f }, { data: n }] = await Promise.all([
        api.get("/issues"), api.get("/facilities"), api.get("/nps"),
      ]);
      setIssues(i); setFacs(f); setNps(n);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[issues] load failed:", e?.message || e);
      toast.error("Could not load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = issues.filter((i) => filter === "all" ? true : filter === "open" ? i.status !== "resolved" : i.status === filter);
  const facMap = Object.fromEntries(facs.map((f) => [f.id, f.name]));
  const npMap = Object.fromEntries(nps.map((n) => [n.id, n.name]));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/issues", { ...form, facility_id: form.facility_id || null, np_id: form.np_id || null });
      toast.success("Issue added"); setOpen(false);
      setForm({ title: "", description: "", severity: "normal", status: "open", facility_id: "", np_id: "", due_date: "" }); load();
    } catch { toast.error("Failed"); }
  };
  const resolve = async (i) => { await api.put(`/issues/${i.id}`, { status: "resolved" }); load(); };
  const remove = async (i) => { if (!window.confirm("Remove?")) return; await api.delete(`/issues/${i.id}`); load(); };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Workflow</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">Issues</h1>
          <p className="text-sm text-[#5C5850] mt-1.5">Track what's blocking coverage across facilities and NPs.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="add-issue-btn">
          <Plus className="h-4 w-4 mr-2"/> New issue
        </Button>
      </div>

      <div className="mt-6 flex gap-2 text-xs">
        {[["open", "Open"], ["in_progress", "In progress"], ["resolved", "Resolved"], ["all", "All"]].map(([v, l]) => (
          <button
            key={v} onClick={() => setFilter(v)}
            className={`px-3 py-2 rounded-md border transition-colors ${filter === v ? "bg-[#4A6B5D] text-white border-[#4A6B5D]" : "bg-white border-[#E5E0D8] text-[#5C5850] hover:bg-[#F0EBE1]"}`}
          >{l}</button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {filtered.length === 0 && (
          <div className="bg-white border border-[#E5E0D8] rounded-md p-10 text-center text-[#8A847A] text-sm">
            <AlertOctagon className="h-8 w-8 mx-auto opacity-60 mb-2"/>
            No issues here.
          </div>
        )}
        {filtered.map((i) => (
          <div key={i.id} className="bg-white border border-[#E5E0D8] rounded-md p-4 flex items-start gap-3" data-testid={`issue-${i.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-[#292723]">{i.title}</span>
                <StatusPill status={SEVERITY_TONE[i.severity] || "gray"} label={i.severity} />
                <StatusPill status={STATUS_TONE[i.status] || "gray"} label={i.status} />
              </div>
              {i.description && <div className="text-sm text-[#5C5850] mt-1">{i.description}</div>}
              <div className="text-xs text-[#8A847A] mt-2 flex gap-3">
                {i.facility_id && facMap[i.facility_id] && <Link to={`/facilities/${i.facility_id}`} className="hover:text-[#4A6B5D]">🏢 {facMap[i.facility_id]}</Link>}
                {i.np_id && npMap[i.np_id] && <Link to={`/nps/${i.np_id}`} className="hover:text-[#4A6B5D]">👩‍⚕️ {npMap[i.np_id]}</Link>}
                {i.due_date && <span>Due {i.due_date}</span>}
              </div>
            </div>
            {i.status !== "resolved" && <Button variant="ghost" size="sm" onClick={() => resolve(i)} className="text-[#507A5E]"><CheckCircle2 className="h-4 w-4"/></Button>}
            <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-[#C17D6E]"><Trash2 className="h-4 w-4"/></Button>
          </div>
        ))}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader><SheetTitle className="heading text-2xl">New issue</SheetTitle></SheetHeader>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]"/>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5 bg-white border-[#E5E0D8]"/>
            </div>
            <SelectField label="Severity" value={form.severity} onChange={(v) => setForm({ ...form, severity: v })} options={[{value:"low",label:"Low"},{value:"normal",label:"Normal"},{value:"high",label:"High"},{value:"critical",label:"Critical"}]} />
            <SelectField label="Facility" value={form.facility_id} onChange={(v) => setForm({ ...form, facility_id: v })} options={[{value:"",label:"— none —"}, ...facs.map((f) => ({value: f.id, label: f.name}))]} />
            <SelectField label="NP" value={form.np_id} onChange={(v) => setForm({ ...form, np_id: v })} options={[{value:"",label:"— none —"}, ...nps.map((n) => ({value: n.id, label: n.name}))]} />
            <div><Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#4A6B5D] hover:bg-[#3E594D]">Create</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
